/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ViewEncapsulation, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  BaseService,
  ISortModel,
  NotificationService,
  ProxAgFns,
  SaveAndQueryComponent,
  StatusService,
  GridOptionsExt,
  RowSelectionMultipleCheckbox,
  ResponsiveService,
} from '@core';
import {
  AccountAddress,
  AccountUser,
  Gender,
  ProgramUserGroup,
  ProgramUserGroupMap,
  ProximityUser,
  ActiveStatus,
  ActiveStatusEnum,
  ShippingUserGroup,
  ShippingUserGroupMap,
  ProximityRightEnum,
  Account,
  ProximityRole,
  ProximityUserNote,
  ProgramUserGroupExclusion,
} from '@models';
import { AccountDbQueryService, AccountUserMatch } from './../services/account-db-query.service';
import { AccountDbSaveService } from './../services/account-db-save.service';

import { StringFns, UtilFns } from '@utils';
import { EntityError } from 'breeze-client';
import { DialogService } from 'primeng/dynamicdialog';
import { ColDef, GetRowIdParams, GridReadyEvent } from '@ag-grid-community/core';
import { EntityFns } from '@data';
import { ProgramUserGroupPickerDialogComponent, PugPickerParams } from '../approval-trees/program-user-group-picker.dialog';
import { ShippingUserGroupFinderDialog } from './shipping-user-group-finder.dialog';
import * as _ from 'lodash';
import { ProximityUserNotesDialogComponent } from '../proximity-user-notes-dialog';
import { AccountUserProgramsDialogComponent } from './account-user-programs-dialog.component';
import { ProgramPugChooserDialogComponent } from './program-pug-chooser.dialog';
import { PugProgramAccessDialogComponent, PugProgramAccessItem } from './pug-program-access.dialog';
import { ProgramAccessItem, ProgramAccessRow, ProgramRulesService } from '../services/program-rules.service';

@Component({
  selector: 'prox-account-user-frm',
  templateUrl: './account-user-frm.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      prox-account-user-frm {
        .p-tabpanels {
          padding: 0 !important;
        }

        /* Ensure tab panel content area has proper height for scrolling */
        .p-tabview-panels {
          overflow: hidden;
          height: 100%;
        }

        .p-tabpanel {
          height: 100%;
        }

        /* Style the scroll panel in address tabs */
        .p-tabpanel .p-scrollpanel {
          height: 100%;
        }

        .p-tabpanel .p-scrollpanel .p-scrollpanel-content {
          padding-right: 12px;
        }
      }

      /* Access-state text colors for the Program User Groups and Program Access grids.
         Scoped under both ag-theme classes so they beat the theme's own .ag-cell { color }
         rule (0,2,0 specificity); see memory note
         "ag-grid-cell-text-color-needs-theme-scoped-selector".

         RED ('pug-access-blocked') MEANS SOMETHING IS ACTUALLY BLOCKED, and only that:
           - two active groups on one program, so the user cannot order it; or
           - Type "Not Set", so the program (and the account) cannot activate.
         A deliberate exclusion stays neutral grey — it is a chosen configuration, and coloring
         chosen states as errors is what made the old Full/Partial/No Access palette worth
         ignoring. */
      .ag-theme-alpine .pug-access-ok,
      .ag-theme-alpine-dark .pug-access-ok,
      .ag-theme-balham .pug-access-ok,
      .ag-theme-balham-dark .pug-access-ok { color: #16a34a; font-weight: 500; }
      .ag-theme-alpine .pug-access-blocked,
      .ag-theme-alpine-dark .pug-access-blocked,
      .ag-theme-balham .pug-access-blocked,
      .ag-theme-balham-dark .pug-access-blocked { color: #dc2626; font-weight: 600; }
      .ag-theme-alpine .pug-access-warn,
      .ag-theme-alpine-dark .pug-access-warn,
      .ag-theme-balham .pug-access-warn,
      .ag-theme-balham-dark .pug-access-warn { color: #d97706; font-weight: 500; }
      .ag-theme-alpine .pug-access-neutral,
      .ag-theme-alpine-dark .pug-access-neutral,
      .ag-theme-balham .pug-access-neutral,
      .ag-theme-balham-dark .pug-access-neutral { color: #6b7280; }
      .ag-theme-alpine .pug-access-inactive,
      .ag-theme-alpine-dark .pug-access-inactive,
      .ag-theme-balham .pug-access-inactive,
      .ag-theme-balham-dark .pug-access-inactive { color: #6b7280; font-style: italic; }
    `,
  ],
})
export class AccountUserFrmComponent extends SaveAndQueryComponent {
  accountId!: string;
  accountName!: string;
  accountUserId!: string;
  accountUser: AccountUser = new AccountUser();
  account!: Account;
  proximityUser: ProximityUser = new ProximityUser();
  activeStatuses: ActiveStatus[] = [];

  // Responsive splitter sizes: [35, 65] on large screens, [50, 50] on small screens
  splitterPanelSizes = computed(() => (this.responsiveService.lt2xl() ? [50, 50] : [37, 63]));

  // Responsive grid width: 50% on large screens, 100% on small screens
  tabGridWidthClass = computed(() => (this.responsiveService.lt2xl() ? 'w-full' : 'w-1/2'));

  // Responsive address form width: 2/3 on large screens, 100% on small screens
  addressFormWidthClass = computed(() => (this.responsiveService.lt2xl() ? 'w-full' : 'w-2/3'));

  genders: Gender[] = [];
  pugmGridOptions!: GridOptionsExt;
  pugms: ProgramUserGroupMap[] = [];
  /** Tab index — the blocked-programs banner switches to the Program Access tab. */
  tabIndex = 0;

  /**
   * One row per Program this user can reach through any of their PUGs. This is the view that
   * answers "what can this user actually order, and through which group?" — the PUG grid alone
   * cannot, because membership is keyed by PUG while the ordering invariant is keyed by Program.
   * See Docs/program-user-group-access.md.
   */
  programAccessRows: ProgramAccessRow[] = [];
  paGridOptions!: GridOptionsExt;
  /** The user's exclusions, held so the chooser can delete as well as create them. */
  private pugExclusions: ProgramUserGroupExclusion[] = [];
  /** Raw per-(program x PUG) items, kept so the rows can be rebuilt when a membership is
   *  removed without waiting for a save + refetch. */
  private accessItems: ProgramAccessItem[] = [];

  /** Programs where two or more of the user's PUGs are active — always a misconfiguration. */
  get blockedProgramCount(): number {
    return this.programAccessRows.filter((r) => r.state === 'Blocked').length;
  }

  shippingUserGroups: ShippingUserGroup[] = [];
  sugmGridOptions!: GridOptionsExt;
  sugms: ShippingUserGroupMap[] = [];
  formTitle = '* New Account User *';
  isBeingAdded!: boolean;

  accountUserRole!: ProximityRole;

  // Scoped-admin support: when the caller is a non-PAA AccountAdministrator, these track
  // their supervised-PUG set and the lookup-first add-mode state. PAAs have unrestricted
  // access and these stay at their defaults (supervisedPugIds=null = "no filter").
  isScopedAdmin = false;
  supervisedPugIds: Set<string> | null = null;
  outOfScope = false;
  existingUserMatch: AccountUserMatch | null = null;
  emailLookupBusy = false;

  // Snapshot of First Name taken on blur — drives the add-mode Helpful Hints
  // personalization without churning the panel on every keystroke.
  firstNameForHints = '';

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbQueryService: AccountDbQueryService,
    override dbSaveService: AccountDbSaveService,
    public pngDialogService: DialogService,
    private statusService: StatusService,
    private responsiveService: ResponsiveService,
    private notificationService: NotificationService,
    private programRulesService: ProgramRulesService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    this.accountUserId = params['accountUserId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    UtilFns.assertNonEmptyString(this.accountUserId, 'accountUserId');

    // Take the page off screen, and claim add-mode, BEFORE the first await rather than after it.
    //
    // route.url fires again whenever the router re-activates this component — navigating to /add
    // while already sitting on /add does it — and Angular reuses the instance, so a re-run used to
    // leave the whole page live on screen through every query below. Through that window
    // isBeingAdded still held the `false` that afterSave left behind, and createNewUser has not
    // run yet, so the access buttons' two guards (isBeingAdded, hasChanges) both read
    // "editing is allowed" — hasChanges cannot see a new user because createNewUser leaves it
    // Unchanged.
    //
    // An admin clicked View/Edit inside that window. The dialog built its items from the saved
    // user's rows, and by the time they pressed Save createNewUser had replaced this.accountUser
    // with a brand new, never-persisted record — so the exclusions were written against an
    // AccountUser id that has no row. The save failed on
    // FK_ProgramUserGroupExclusions_AccountUsers_AccountUserId (Sentry 7763687008, WSSC,
    // 2026-09-30). The identity checks after each dialog await below close the other half of
    // this: an overlay already open survives the page going off screen.
    this.isPageReady = false;
    this.isBeingAdded = this.accountUserId === 'add';

    let accountUserRoles: ProximityRole[];
    // No account-wide ProgramUserGroup load here: the attach picker fetches its own flat list
    // over HTTP, so the page no longer pays for every PUG on the account at open time.
    [this.account, this.genders, this.activeStatuses, this.shippingUserGroups, accountUserRoles] =
      await Promise.all([
        this.dbQueryService.getAccountById(this.accountId),
        this.dbQueryService.getAll(Gender),
        this.dbQueryService.getAll(ActiveStatus),
        this.dbQueryService.getShippingUserGroups(this.accountId),
        this.dbQueryService.getAccountUserRoles(this.accountId),
      ]);

    UtilFns.assertNonNull(this.account, 'Account');
    UtilFns.assert(accountUserRoles.length > 0, 'Account User Role');
    this.accountUserRole = accountUserRoles[0];

    // (isBeingAdded is set at the top of this method, before the awaits above.)

    // Scoped-admin determination: a non-PAA AccountAdministrator only manages users whose
    // PUGs sit under their nodes in the Proximity Organization Tree. PAAs (and anyone with
    // CanManageAccountOrganization) keep unrestricted access; supervisedPugIds=null is
    // the "no filter" sentinel used downstream by the PUG-picker and per-row gates.
    if (this.authUser?.accountAdminId && !this.hasRight(ProximityRightEnum.CanManageAccountOrganization)) {
      this.isScopedAdmin = true;
      const ids = await this.dbQueryService.getSupervisedPugsForAdmin(this.authUser.accountAdminId);
      this.supervisedPugIds = new Set(ids);
    }

    if (this.isBeingAdded) {
      this.createNewUser();
    } else {
      this.accountUser = await this.dbQueryService.getAccountUserById(this.accountUserId);
      this.proximityUser = this.accountUser?.proximityUser;
      this.formTitle = this.accountUser.proximityUser.fullName;

      // Block render when a scoped admin lands on a user outside their supervised scope.
      // Server-side BeforeSaveHandler will also reject saves, but failing fast in the UI
      // avoids the admin getting halfway into editing fields they cannot save.
      if (this.isScopedAdmin) {
        const canEdit = await this.dbQueryService.canEditAccountUser(this.authUser!.accountAdminId!, this.accountUserId);
        this.outOfScope = !canEdit;
      }
    }
    this.pugms = this.accountUser.programUserGroupMaps ?? [];
    this.sugms = this.accountUser.shippingUserGroupMaps ?? [];

    await this.loadProgramAccess();

    this.paGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPaGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => (rowIdParams.data as ProgramAccessRow).programId,
      overlayNoRowsTemplate:
        '<span class="ag-overlay-no-rows-center">This user is not in any Program User Group that reaches a program.</span>',
    });

    this.pugmGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPugmGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const pugm = rowIdParams.data as ProgramUserGroupMap;
        return pugm.programUserGroupId;
      },
    });

    this.sugmGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onSugmGridReady,
      rowModelType: 'clientSide',
      getRowId: (rowIdParams: GetRowIdParams) => {
        const sugm = rowIdParams.data as ShippingUserGroupMap;
        return sugm.shippingUserGroupId;
      },
    });

    // To test isFormDisabled logic
    /*      if (this.accountUser.proximityUser.lastName.startsWith('C')) {
       this.isFormDisabled = true;
     } */

    this.setTitle('Account User for ' + this.account.name);
    this.isPageReady = true;
  }

  isAccountAdmin() {
    return this.proximityUser.accountAdmins.length > 0;
  }

  isReadOnly() {
    if (this.statusService.getWorkingStatus(this.account).isReadOnly) return true;
    // PAA/GlobalAdmin path: unrestricted write authority.
    if (this.hasRight(ProximityRightEnum.CanManageAccountOrganization)) return false;
    // Scoped AccountAdmin path: writable iff in add-mode (R1 enforces supervised-PUG at
    // save) or editing a user already determined to be in scope (outOfScope handles the
    // miss). Without this branch, a scoped admin sees the form but every field is disabled.
    if (this.isScopedAdmin && (this.isBeingAdded || !this.outOfScope)) return false;
    return true;
  }

  /** Enables the "Change..." link on the (disabled) email field — see UserFrmComponent. */
  get canChangeEmail(): boolean {
    if (this.isBeingAdded) return false;
    if (this.hasChanges()) return false;
    return !this.isReadOnly();
  }

  statusMessage() {
    // Mirror isReadOnly's authorization rules: PAA (CanManageAccountOrganization) OR
    // a scoped AccountAdmin viewing an in-scope user is authorized. The legacy "Not
    // authorized" string was visible to scoped admins editing in-scope users before
    // isReadOnly was updated to honor CanEditUserByApprovalTree.
    if (!this.hasRight(ProximityRightEnum.CanManageAccountOrganization) && !(this.isScopedAdmin && !this.outOfScope)) {
      return 'Not authorized';
    }

    if (this.account.getStatusId() == ActiveStatusEnum.Active) {
      return 'Account is Active.  Account Users are Editable.';
    }
    return this.statusService.getWorkingStatus(this.accountUser).statusDisplay;
  }

  okToDelete() {
    return !this.isReadOnly();
  }

  override get entity() {
    return this.accountUser;
  }

  toggleActivate() {
    if (this.accountUser.proximityUser.activeStatusId === ActiveStatusEnum.Hold) {
      this.accountUser.proximityUser.activeStatusId = ActiveStatusEnum.Active;
    } else if (this.accountUser.proximityUser.activeStatusId === ActiveStatusEnum.Active) {
      this.accountUser.proximityUser.activeStatusId = ActiveStatusEnum.Hold;
    }
    this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: this.accountUser.proximityUserId,
      isSystemGenerated: true,
      noteTs: new Date(),
      note: `Account User Active Status changed to '${this.accountUser.proximityUser.activeStatus.name}'`,
    });

    if (this.accountUser.proximityUser.activeStatusId === ActiveStatusEnum.Hold) {
      const userLink = `/account/accounts/${this.accountId}/manage/account-users/${this.accountUser.id}`;
      this.notificationService.add(
        'warn',
        'Account User On Hold',
        `<a href="${userLink}">${this.accountUser.proximityUser.fullName}</a> is on Hold. Remember to re-activate when editing is complete.`,
        undefined,
        `account-user-hold:${this.accountUser.id}`,
      );
    } else {
      this.notificationService.resolveByKey(`account-user-hold:${this.accountUser.id}`);
    }
  }

  async viewNotes() {
    const r = await ProximityUserNotesDialogComponent.openDialog(this.pngDialogService, {
      proximityUser: this.accountUser.proximityUser,
      isEditable: !this.isReadOnly(),
    });
  }

  canSendInvite(): boolean {
    if (this.isBeingAdded) return false;
    if (this.hasChanges()) return false;
    // When AllowAccountUserAccess is false, self-service login is off — sending an invite
    // would land the user in a broken flow. Server also refuses in this state (see
    // IdentityHelper.SendConfirmationLinkAsync → SendInviteOutcome.AccessDisabled).
    if (!this.account.allowAccountUserAccess) return false;
    return !!this.proximityUser?.email?.trim();
  }

  async onSendInvite() {
    if (!this.canSendInvite()) return;
    const status = await this.dbSaveService.postSendUserInvite(this.proximityUser.id);
    if (status === 'Sent') {
      this.toastr.success('Invite email was sent to ' + this.proximityUser.name);
    } else if (status === 'AlreadyConfirmed') {
      this.toastr.info(
        `${this.proximityUser.name}'s email is already confirmed — no invite was sent.`,
        'Email Already Confirmed',
      );
    }
  }

  async onViewPrograms() {
    await AccountUserProgramsDialogComponent.openDialog(this.pngDialogService, {
      accountUser: this.accountUser,
    });
  }

  onViewLedgers() {
    this.router.navigate(['account-admin', 'accounts', this.accountId, 'manage', 'user-insights', this.accountUser.id]);
  }

  statusButtonLabel() {
    if (!this.accountUser?.proximityUser) return '';
    if (this.accountUser.proximityUser.activeStatusId == ActiveStatusEnum.Active) {
      return 'Place Account User on Hold';
    } else if (this.accountUser.proximityUser.activeStatusId == ActiveStatusEnum.Hold) {
      return 'Activate Account User';
    }
    return 'Contact Proximity Support';
  }

  onPugmGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPugmColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPugmColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'programUserGroup.name',
        ...AgFns.flexWidth(200),
      },
      // A plain count, not a verdict.
      //
      // This column used to read "Full Access / Partial Access / No Access", derived from this
      // PUG's exclusion count in isolation. That was actively misleading: "Full Access" only ever
      // meant "no exclusions on this group", and two groups both reading a green "Full Access" is
      // the exact shape of the one state that BLOCKS the user from ordering. A per-PUG label
      // cannot express a per-Program invariant, so it no longer tries — the Program Access tab
      // states it instead, and this column just reports the facts behind it.
      {
        headerName: 'What This Group Gives This User',
        colId: 'pugmPrograms',
        valueGetter: (p) => this.getPugAccessSummary(p.data as ProgramUserGroupMap).label,
        cellClass: (p) => this.getPugAccessSummary(p.data as ProgramUserGroupMap).cls,
        sortable: false,
        ...AgFns.flexWidth(260),
      },
      {
        ...AgFns.createButtonProps('', this.onEditPugPrograms.bind(this), {
          style: 'text',
          label: 'View/Edit',
          canDisplay: (pugm: ProgramUserGroupMap) => this.reachableRowsForPug(pugm.programUserGroupId).length > 0,
          calcDisabled: () => this.isAccessEditingBlocked(),
        }),
        ...AgFns.fixWidth(110),
        suppressHeaderMenuButton: true,
        colId: 'pugProgramsBtn',
      } as ColDef,
      ProxAgFns.getEntityDeleteColDef(this.onPugmDelete.bind(this), { canDisplay: this.canDeletePugm.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'programUserGroup.name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  /**
   * Still editing the record we opened the dialog for?
   *
   * Every dialog on this page is an overlay, so it stays up even when updateFromParams re-runs and
   * takes the page underneath it off screen. That re-run replaces this.accountUser — with a fresh
   * never-saved one in add-mode, or with a reloaded one otherwise — and anything written after the
   * dialog closes would land on the wrong record. Writing a child row against a new unsaved user
   * is worse than wrong: there is no AccountUsers row for it to point at, and createNewUser leaves
   * that user Unchanged so the save would not create one. See the note in updateFromParams.
   */
  private stillEditing(accountUserAtOpen: AccountUser): boolean {
    return this.accountUser === accountUserAtOpen;
  }

  /** Programs this group reaches for this user, dead ones included (the dialog labels them). */
  private reachableRowsForPug(pugId: string): ProgramAccessRow[] {
    return this.programAccessRows.filter((r) => r.choices.some((c) => c.programUserGroupId === pugId));
  }

  /**
   * Group-first editor: one group, every program it reaches, include/exclude each.
   *
   * The program-first chooser answers "which group is correct for this program"; this answers
   * "which of this group's programs should this user actually get". Same exclusion rows, opposite
   * axis — an admin looking at a membership row wants this one.
   */
  async onEditPugPrograms(pugm: ProgramUserGroupMap) {
    if (this.isAccessEditingBlocked()) return;

    const pugId = pugm.programUserGroupId;
    const rows = this.reachableRowsForPug(pugId);
    const items: PugProgramAccessItem[] = rows.map((row) => {
      const mine = row.choices.find((c) => c.programUserGroupId === pugId)!;
      // Every OTHER group currently holding the active slot. Re-including here takes that slot,
      // so the dialog names them and Save deactivates them.
      const rivals = row.choices.filter((c) => c.programUserGroupId !== pugId && c.isAllowed);
      return {
        programId: row.programId,
        programName: row.programName,
        statusLabel: row.statusLabel,
        isExcluded: mine.isExcluded,
        conflictingGroupNames: rivals.map((r) => r.programUserGroupName),
        isDead: row.state === 'Expired' || row.state === 'ProgramInactive',
      };
    });

    const accountUserAtOpen = this.accountUser;
    const result = await PugProgramAccessDialogComponent.openDialog(this.pngDialogService, {
      programUserGroupName: pugm.programUserGroup.name,
      userFullName: this.proximityUser.fullName,
      isReadOnly: this.isReadOnly(),
      items,
    });
    if (!result) return;
    if (!this.stillEditing(accountUserAtOpen)) return;

    const excluded = new Set(result.excludedProgramIds);
    items.forEach((item) => this.setExclusion(item.programId, pugId, excluded.has(item.programId)));

    // Taking the active slot means giving it up elsewhere. Only one group may be active for a
    // program; leaving the others active would make the program unorderable rather than
    // reassigned, which is the state the dialog's warning promised to avoid.
    //
    // Every other allowed group is deactivated, not just the first: on valid data there is only
    // one, so this is identical, and on data that is already broken it repairs rather than
    // leaving a second conflict behind. Same rule the program-first chooser applies.
    const switching = new Set(result.switchingProgramIds);
    let deactivated = 0;
    rows
      .filter((row) => switching.has(row.programId))
      .forEach((row) => {
        row.choices
          .filter((c) => c.programUserGroupId !== pugId && c.isAllowed)
          .forEach((rival) => {
            this.setExclusion(row.programId, rival.programUserGroupId, true);
            deactivated++;
          });
      });

    const noteParts: string[] = [
      excluded.size === 0
        ? `Included in all programs for Program User Group: ${pugm.programUserGroup.name}`
        : `Excluded from ${excluded.size} program(s) for Program User Group: ${pugm.programUserGroup.name}`,
    ];
    if (deactivated > 0) {
      noteParts.push(
        `${pugm.programUserGroup.name} made the active group for ${switching.size} program(s); ` +
          `${deactivated} other group assignment(s) deactivated.`,
      );
    }
    await this.commitAccessChange(noteParts.join(' '));
  }

  /**
   * Access edits are self-contained: the button is disabled while the form is dirty, so a save
   * here can only ever commit the exclusions this dialog just wrote. That is what lets the
   * dialog offer a plain Save / Cancel instead of leaving pending changes on the page — the
   * page is guaranteed to be back at an untouched entity either way.
   */
  isAccessEditingBlocked(): boolean {
    return this.isReadOnly() || this.isBeingAdded || this.hasChanges();
  }

  /** Saves the exclusion edits immediately, then rebuilds both grids from saved state. */
  private async commitAccessChange(note: string) {
    this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: this.accountUser.proximityUserId,
      note,
      isSystemGenerated: true,
      noteTs: new Date(),
    });

    try {
      await this.dbSaveService.saveChanges();
    } catch {
      // saveChanges(shouldRollbackOnError = true) has already rolled the unit of work back, so
      // the page is at an untouched entity either way — just say so.
      await this.dialogService.okDialog(
        'Could not save',
        'The change to this user’s program access was not saved. Nothing has been altered.',
      );
    }

    await this.loadProgramAccess();
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
    AgFns.refreshGrid(this.paGridOptions, this.programAccessRows);
  }

  /**
   * What this membership actually does for THIS user, in a sentence.
   *
   * An earlier version of this column showed a bare count ("1 program"), taken from the server's
   * per-PUG ProgramCount. That number is a fact about the GROUP — how many programs its approval
   * trees touch, account-wide — on a grid whose rows are about one user. It counted programs the
   * user is excluded from, and expired and inactive ones, so it could not answer the only
   * question the row raises: what does being in this group get this person?
   *
   * So the column names the program when there is exactly one, and states the outcome when there
   * are several. Dead programs (expired / inactive) are excluded from every count here — they
   * will never route another order, and padding the numbers with them is how "6 programs" comes
   * to mean nothing.
   */
  getPugAccessSummary(pugm: ProgramUserGroupMap | undefined): { label: string; cls: string } {
    const pugId = pugm?.programUserGroupId;
    if (!pugId) return { label: '', cls: '' };

    // An Inactive PUG routes no orders at all, whatever its program count says — most often a
    // consolidation loser whose memberships are left in place so in-flight orders can finish.
    if (pugm?.programUserGroup?.activeStatusId === ActiveStatusEnum.Inactive) {
      return {
        label: pugm.programUserGroup.consolidatedIntoPugId
          ? 'Nothing — group was consolidated into another'
          : 'Nothing — group is inactive',
        cls: 'pug-access-inactive',
      };
    }

    // Newly attached and not yet saved: the server has not told us what it reaches, so do not
    // claim it reaches nothing.
    if (pugm?.entityAspect?.entityState?.isAdded()) {
      return { label: 'Save to see what this group gives', cls: 'pug-access-neutral' };
    }

    const live = this.programAccessRows.filter((r) => r.state !== 'Expired' && r.state !== 'ProgramInactive');
    const reachable = live.filter((r) => r.choices.some((c) => c.programUserGroupId === pugId));
    if (reachable.length === 0) {
      return { label: 'Nothing — no active program uses this group', cls: 'pug-access-neutral' };
    }

    const blocked = reachable.filter((r) => r.state === 'Blocked');
    if (blocked.length > 0) {
      const label =
        blocked.length === 1
          ? `Blocked on ${blocked[0].programName} — two groups compete`
          : `Blocked on ${blocked.length} programs — two groups compete`;
      return { label, cls: 'pug-access-blocked' };
    }

    // Programs this group actually governs for this user: the user is allowed through THIS pug.
    const governed = reachable.filter(
      (r) => r.choices.find((c) => c.programUserGroupId === pugId)?.isAllowed,
    );

    if (governed.length === 0) {
      const label =
        reachable.length === 1
          ? `Nothing — excluded from ${reachable[0].programName}`
          : `Nothing — excluded from all ${reachable.length} of its programs`;
      return { label, cls: 'pug-access-neutral' };
    }

    if (governed.length === 1) {
      const suffix = reachable.length > 1 ? ` (1 of ${reachable.length})` : '';
      return { label: `May order from ${governed[0].programName}${suffix}`, cls: 'pug-access-ok' };
    }

    const suffix = governed.length === reachable.length ? '' : ` of ${reachable.length}`;
    return { label: `May order from ${governed.length}${suffix} programs`, cls: 'pug-access-ok' };
  }

  // ---------------------------------------------------------------------------------------
  // Program Access
  // ---------------------------------------------------------------------------------------

  /**
   * Loads the per-Program access rows, which both tabs read from.
   *
   * Called on init AND after every save. The access state used to be fetched once in
   * updateFromParams and never again, so a PUG attached and saved kept rendering stale until
   * the page was reloaded.
   */
  async loadProgramAccess(): Promise<void> {
    if (this.isBeingAdded) {
      this.accessItems = [];
      this.programAccessRows = [];
      this.pugExclusions = [];
      return;
    }

    const [accessItems, exclusions] = await Promise.all([
      this.programRulesService.getProgramAccessItemsForUser(this.accountUser),
      this.dbQueryService.getProgramUserGroupExclusionsForAccountUser(this.accountUserId),
    ]);

    this.accessItems = accessItems;
    this.pugExclusions = exclusions;
    this.recomputeProgramAccessRows();
  }

  /**
   * Rebuilds the Program Access rows from the items already in memory, counting only the
   * memberships still on the user.
   *
   * Removing a membership changes the answer for every program that group reached — most
   * visibly, a program that was Blocked by two groups stops being blocked the moment one of
   * them is deleted. The delete is a pending change, so there is nothing to refetch yet; the
   * rows have to be recomputed locally or the surviving row keeps claiming a conflict with a
   * group that is on its way out.
   */
  private recomputeProgramAccessRows() {
    const liveIds = new Set(
      this.pugms.filter((p) => !p.entityAspect.entityState.isDeleted()).map((p) => p.programUserGroupId),
    );
    const items = this.accessItems.filter((i) => liveIds.has(i.programUserGroupId));
    this.programAccessRows = this.programRulesService.buildProgramAccessRows(items);
  }

  /** Keeps the membership grid, the access rows and the banner in step after a membership edit. */
  private syncPugmsAndAccess() {
    this.pugms = this.accountUser.programUserGroupMaps;
    this.recomputeProgramAccessRows();
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
    AgFns.refreshGrid(this.paGridOptions, this.programAccessRows);
  }

  onPaGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { headerName: 'Program', field: 'programName', ...AgFns.flexWidth(220) },
      {
        // "Not Set" is red because it is a blocker, not a description: a group with neither an
        // allowance nor issuances will stop the program — and so the account — from activating.
        headerName: 'Type',
        field: 'programTypeLabel',
        cellClass: (p) => ((p.data as ProgramAccessRow).programTypeLabel === 'Not Set' ? 'pug-access-blocked' : ''),
        ...AgFns.fixWidth(150),
      },
      {
        headerName: 'Routed Through',
        field: 'routedThroughName',
        cellClass: (p) => ((p.data as ProgramAccessRow).state === 'Blocked' ? 'pug-access-blocked' : ''),
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Status',
        field: 'statusLabel',
        cellClass: (p) => this.getProgramAccessCellClass(p.data as ProgramAccessRow),
        ...AgFns.fixWidth(200),
      },
      {
        ...AgFns.createButtonProps('', this.onChooseGoverningPug.bind(this), {
          style: 'text',
          calcLabel: (row: ProgramAccessRow) => (row.state === 'Blocked' ? 'Choose' : 'Change'),
          canDisplay: (row: ProgramAccessRow) => row.canChoose,
          calcDisabled: () => this.isAccessEditingBlocked(),
        }),
        ...AgFns.fixWidth(110),
        suppressHeaderMenuButton: true,
        colId: 'chooseBtn',
      } as ColDef,
    ];
    const sortModel: ISortModel = [{ colId: 'programName', sort: 'asc' }];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  /**
   * Tone per Docs/program-user-group-access.md §6.4. Red is reserved for `Blocked`, the only
   * state that is always wrong. A deliberate exclusion is neutral — painting a chosen
   * configuration red is what trained admins to ignore red on this page in the first place.
   */
  getProgramAccessCellClass(row: ProgramAccessRow | undefined): string {
    switch (row?.state) {
      case 'CanOrder':         return 'pug-access-ok';
      case 'Blocked':          return 'pug-access-blocked';
      case 'GroupInactive':    return 'pug-access-warn';
      case 'ProgramInactive':  return 'pug-access-warn';
      default:                 return 'pug-access-neutral';
    }
  }

  /** Jump from the blocked-programs banner to the tab that can fix it. */
  goToProgramAccess() {
    this.tabIndex = 1;
  }

  /**
   * Opens the chooser and applies the result as exclusions.
   *
   * Picking group W on program P means: W is included, every other candidate is excluded.
   * "None" excludes them all — a legitimate configuration, not a failure. Writes go through
   * the page's own Breeze unit of work, so they join the normal Save / Undo flow rather than
   * committing behind the admin's back.
   */
  async onChooseGoverningPug(row: ProgramAccessRow) {
    if (this.isAccessEditingBlocked()) return;

    const accountUserAtOpen = this.accountUser;
    const result = await ProgramPugChooserDialogComponent.openDialog(this.pngDialogService, {
      row,
      userFullName: this.proximityUser.fullName,
      isReadOnly: this.isReadOnly(),
      supervisedPugIds: this.isScopedAdmin ? this.supervisedPugIds : null,
    });
    if (!result) return;
    if (!this.stillEditing(accountUserAtOpen)) return;

    for (const choice of row.choices) {
      const shouldExclude = choice.programUserGroupId !== result.winnerPugId;
      this.setExclusion(row.programId, choice.programUserGroupId, shouldExclude);
    }

    const winner = row.choices.find((c) => c.programUserGroupId === result.winnerPugId);
    await this.commitAccessChange(
      winner
        ? `Program '${row.programName}' now routes through Program User Group: ${winner.programUserGroupName}`
        : `Excluded from all Program User Groups on program: ${row.programName}`,
    );
  }

  /** Creates or deletes the (program x PUG x user) exclusion, reviving a pending delete if one exists. */
  private setExclusion(programId: string, programUserGroupId: string, shouldExclude: boolean) {
    const existing = this.pugExclusions.find(
      (x) => x.programId === programId && x.programUserGroupId === programUserGroupId,
    );

    if (shouldExclude) {
      if (existing && existing.entityAspect.entityState.isDeleted()) {
        existing.entityAspect.rejectChanges();
        return;
      }
      if (existing) return;
      const revived = this.dbSaveService.uow.undoIfDeleted(ProgramUserGroupExclusion, [
        programId,
        programUserGroupId,
        this.accountUser.id,
      ]);
      const created =
        revived ??
        this.dbSaveService.createEntity(ProgramUserGroupExclusion, {
          programId,
          programUserGroupId,
          accountUserId: this.accountUser.id,
        });
      this.pugExclusions.push(created);
      return;
    }

    if (existing && !existing.entityAspect.entityState.isDeleted()) {
      EntityFns.deleteOrDetach(existing.entityAspect);
      _.remove(this.pugExclusions, (x) => x === existing);
    }
  }


  /**
   * Per-row gate for the PUG-membership delete column. Scoped admins can only detach
   * memberships on PUGs they supervise — out-of-scope rows show no delete icon (server
   * enforces the same rule in BeforeSaveHandler).
   */
  canDeletePugm(pugm: ProgramUserGroupMap): boolean {
    if (!this.okToDelete()) return false;
    if (!this.isScopedAdmin || !this.supervisedPugIds) return true;
    return this.supervisedPugIds.has(pugm.programUserGroupId);
  }

  async onPugmAttach() {
    const accountUserAtOpen = this.accountUser;
    const usedPugIds = [...new Set(this.accountUser.programUserGroupMaps.map((x) => x.programUserGroupId))];

    // Purpose-built PUG picker (see program-user-group-picker.dialog.ts for the "why".)
    // It hides Inactive PUGs outright (the server refuses those maps), tags Hold/Phaseout, and
    // makes consolidation losers visible but unselectable. The generic finder this replaced showed
    // every PUG at every status with no status column at all, so an admin could attach a user
    // to a dead group and only learn about it from the Access column afterwards.
    // Returns {Id, Name}[] via a plain HTTP call, no Breeze.
    const params: PugPickerParams = {
      accountId: this.accountId,
      excludePugIds: usedPugIds,
      // Scoped admins can only attach PUGs in their supervised set — the server gate would
      // reject anything else, so don't let them pick it in the first place.
      includeOnlyPugIds: this.isScopedAdmin && this.supervisedPugIds ? [...this.supervisedPugIds] : undefined,
    };
    const picked = (await this.dialogService.create(ProgramUserGroupPickerDialogComponent, params, {
      header: 'Select Program User Groups',
      width: 'calc(50vw)',
    })) as { Id: string; Name: string }[];

    if (!picked || picked.length === 0) return;
    if (!this.stillEditing(accountUserAtOpen)) return;

    // The picker bypasses Breeze, so the picked PUGs aren't in the local EntityManager yet.
    // The ProximityUserNote below dereferences pugm.programUserGroup.name, so each picked PUG
    // must be in Breeze cache first. Small n (only the picked ids) — no risk of re-triggering
    // the self-ref materialization bug that made us bypass Breeze in the picker in the first place.
    const pickedIds = picked.map((p) => p.Id);
    await this.dbQueryService.uow
      .createQuery(ProgramUserGroup)
      .where({ id: { in: pickedIds } })
      .execute();

    picked.forEach((pug) => {
      let pugm = this.dbSaveService.uow.undoIfDeleted(ProgramUserGroupMap, [pug.Id, this.accountUser.id]);
      if (pugm == null) {
        pugm = this.dbSaveService.createEntity(ProgramUserGroupMap, {
          programUserGroupId: pug.Id,
          accountUserId: this.accountUser.id,
          joinDate: new Date(),
        });
        this.dbSaveService.createEntity(ProximityUserNote, {
          proximityUserId: pugm.accountUser.proximityUserId,
          note: `Added to Program User Group: ${pugm.programUserGroup.name}`,
          isSystemGenerated: true,
          noteTs: new Date(),
        });
      }
    });
    this.syncPugmsAndAccess();
  }

  async onPugmDelete(pugm: ProgramUserGroupMap) {
    if (this.dbSaveService.hasChanges()) {
      await this.dialogService.okDialog('Cannot Remove User', 'Please save all changes before removing this user from a Program User Group.');
      return;
    }

    // Ask the same question the server's strand-guard asks (CheckProgramUserGroupMapDeletes),
    // BEFORE offering the removal. Any in-flight order under this membership blocks it.
    //
    // This used to cascade-delete the Setup / AwaitingApproval orders into the same save. That
    // never worked: the guard queries the database in BeforeSaveEntities — before any of the
    // save's deletions are applied — and does not exclude JobOrders being deleted in the same
    // saveMap, so it counted the very rows this method had just marked for deletion and rejected
    // the save every time. See server/TestProject/ApiTests/PugmDeleteGuardTest.cs.
    //
    // So the cascade is gone rather than repaired: deleting a user's orders as a side effect of
    // a membership change is a destructive act that has, in practice, never once run. Blocking
    // orders are now named up front and cancelled through the normal cancel flow.
    const blockingOrders = await this.dbQueryService.getUnplacedJobOrdersForUserAndProgramUserGroup(
      pugm.accountUserId,
      pugm.programUserGroupId,
    );
    if (blockingOrders.length > 0) {
      const orderList = blockingOrders
        .map((jo) => `<li>${jo.displayId} — ${jo.friendlyStatusName()}</li>`)
        .join('');
      await this.dialogService.okDialog(
        'Cannot Remove User',
        `This user cannot be removed from '${pugm.programUserGroup.name}' while they have open orders under it.
        <br/><br/>
        <ul>${orderList}</ul>
        Cancel or finish placing ${blockingOrders.length === 1 ? 'this order' : 'these orders'}, then remove the membership.`,
      );
      return;
    }

    const ok = await this.dialogService.askIfOk(
      'Remove user from Program User Group',
      `Are you sure you want to remove this user from '${pugm.programUserGroup.name}'?`,
    );
    if (!ok) return;

    this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: pugm.accountUser.proximityUserId,
      note: `Removed from Program User Group: ${pugm.programUserGroup.name}`,
      isSystemGenerated: true,
      noteTs: new Date(),
    });
    EntityFns.deleteOrDetach(pugm.entityAspect);

    const pugExsToDelete = await this.dbQueryService.getProgramUserGroupExclusionsForProgramUserGroupMap(pugm);
    pugExsToDelete.forEach((pugEx) => EntityFns.deleteOrDetach(pugEx.entityAspect));

    this.syncPugmsAndAccess();
  }

  onSugmGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getSugmColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
  }

  getSugmColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'shippingUserGroup.name',
        ...AgFns.flexWidth(200),
      },
      ProxAgFns.getActiveStatusColDef('shippingUserGroup.activeStatus.name'),

      ProxAgFns.getEntityDeleteColDef(this.onSugmDelete.bind(this), { canDisplay: this.okToDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'shippingUserGroup.name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onSugmAttach() {
    const accountUserAtOpen = this.accountUser;
    const usedSugIds = new Set(this.accountUser.shippingUserGroupMaps.map((x) => x.shippingUserGroupId));
    const unusedShippingUserGroups = this.shippingUserGroups.filter((x) => !usedSugIds.has(x.id));
    const newSugs = await this.dialogService.createFinder(ShippingUserGroupFinderDialog, {
      shippingUserGroups: unusedShippingUserGroups,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    if (!this.stillEditing(accountUserAtOpen)) return;
    const newSugms = newSugs.map((sug) => {
      let sugm = this.dbSaveService.uow.undoIfDeleted(ShippingUserGroupMap, [sug.id, this.accountUser.id]);
      if (sugm == null) {
        sugm = this.dbSaveService.createEntity(ShippingUserGroupMap, {
          shippingUserGroupId: sug.id,
          accountUserId: this.accountUser.id,
        });
      }
      return sugm;
    });
    this.sugms = this.accountUser.shippingUserGroupMaps;
    AgFns.refreshGrid(this.sugmGridOptions, this.sugms);
  }

  async onSugmDelete(sugm: ShippingUserGroupMap) {
    EntityFns.deleteOrDetach(sugm.entityAspect);
    this.sugms = this.accountUser.shippingUserGroupMaps;
    AgFns.refreshGrid(this.sugmGridOptions, this.sugms);
  }

  onAddAddress(type: 'B' | 'S') {
    const addr = this.dbSaveService.createEntity(AccountAddress, {
      accountId: this.accountId,
      isBillingAddress: type == 'B',
      isShippingAddress: type == 'S',
      isPersonalAddress: true,
    });
    if (type == 'B') {
      this.accountUser.billingAccountAddressId = addr.id;
    } else {
      this.accountUser.shippingAccountAddressId = addr.id;
    }
  }

  override async addCrossValidationErrors() {
    // Username check goes through Identity (catches orphan AspNetUsers rows that the
    // Breeze ProximityUsers query cannot see — Amanda Bortz 2026-05-25 repro).
    // Only check on add or when the username actually changed; on edit, the user's own
    // existing row would otherwise be flagged as a collision against itself.
    const usernameWasChanged = this.isBeingAdded || 'username' in this.proximityUser.entityAspect.originalValues;
    let ok = true;
    if (usernameWasChanged) {
      ok = await this.baseService.authService.isUsernameAvailable(this.proximityUser.username);
      if (!ok) {
        this.createValidationError(this.proximityUser, 'username', 'This Login Name has already been taken.');
      }
    }
    // Email uniqueness is enforced by a unique EmailIndex on AspNetUsers (migration
    // 20260525113834_UniqueEmailIndex), so a Breeze-only check would miss orphan rows
    // (see project_email-dup-cleanup-and-migration-bug). Edit-mode collisions are not
    // possible here because the email field is disabled on edit — changes only go
    // through the dedicated "Change E-Mail..." link, which has its own conflict flow.
    if (this.isBeingAdded && this.proximityUser.email != null) {
      ok = await this.baseService.authService.isEmailAvailable(this.proximityUser.email, this.proximityUser.id);
      if (!ok) {
        this.createValidationError(this.proximityUser, 'email', 'This email is already in use by another user.');
      }
    }
  }

  override async beforeSave() {
    this.updateAddressDescription(this.accountUser.billingAccountAddress, 'Billing - ');
    this.updateAddressDescription(this.accountUser.shippingAccountAddress, 'Shipping - ');

    // Every new Account User must arrive with at least one PUGM, regardless of who's
    // creating them — a user with zero PUGs cannot place orders. For a scoped admin,
    // the PUG must be in their supervised set (server-side BeforeSaveHandler enforces
    // the same rule); for a PAA, any PUG in the account satisfies the requirement.
    // Modal dialog rather than a toast — hard-stop validation deserves an acknowledged
    // OK click so the admin can't dismiss it accidentally and assume the save succeeded.
    if (this.isBeingAdded) {
      const addedPugms = this.accountUser.programUserGroupMaps.filter(
        (pugm) => pugm.entityAspect.entityState.isAdded(),
      );
      const supervised = this.supervisedPugIds;
      const ok = this.isScopedAdmin && supervised
        ? addedPugms.some((pugm) => supervised.has(pugm.programUserGroupId))
        : addedPugms.length > 0;
      if (!ok) {
        await this.dialogService.okDialog(
          'Action Required',
          "Click the 'Attach Program User Group' button and select one or more Program User Groups for this user.",
        );
        return false;
      }
    }

    if (this.isBeingAdded) {
      this.proximityUser.proximityRole = this.accountUserRole;
      this.proximityUser.entityAspect.setAdded();
      this.accountUser.entityAspect.setAdded();
    }
    this.formTitle = this.accountUser.proximityUser.fullName;
    return true;
  }

  updateAddressDescription(addr: AccountAddress, prefix = '') {
    if (addr == null) return;

    if (StringFns.isNullEmptyOrWhiteSpace(addr.description)) {
      addr.description = prefix + this.accountUser.proximityUser.fullName;
    }
  }

  override async afterUndo(): Promise<void> {
    // Add-mode Undo: the in-progress new ProximityUser/AccountUser/PUGMs have been
    // detached by rejectChanges and there is nothing to "restore to" — sitting on a
    // blank form would be confusing. Return the admin to the list they came from.
    // Edit-mode Undo keeps the legacy behavior: rebind the now-restored entities so
    // the form reflects the saved state.
    if (this.isBeingAdded) {
      this.navigateBack();
      return;
    }
    this.sugms = this.accountUser.shippingUserGroupMaps;
    this.syncPugmsAndAccess();
    AgFns.refreshGrid(this.sugmGridOptions, this.sugms);
  }

  override async afterSave() {
    const wasAdded = this.isBeingAdded;
    this.isBeingAdded = false;

    // In add-mode accountUserId holds the route sentinel 'add', and the URL does not change when
    // the save lands. Anything that queries by it after this point would send the string 'add' to
    // the server as a Guid. Switch to the real key, which createNewUser() already assigned.
    //
    // This is what produced "Unrecognized Guid format" on the ProgramUserGroupExclusion query for
    // every new user (COTY, 2026-09-08). The save had already committed, so the admin saw a Save
    // Error on a user that existed, retried, and created a duplicate on each attempt.
    this.accountUserId = this.accountUser.id;

    // Program access is server-derived, so it can only be recomputed once the save has landed.
    // Without this the counts and the Program Access rows keep showing pre-save state — a PUG
    // attached and saved rendered as "No programs" until the page was reloaded.
    //
    // Everything from here on is REFRESH, not save. The save is already committed, so a failure
    // here must not surface as a Save Error: an admin told the save failed will do the only
    // sensible thing and add the user again. That is precisely how one bad query turned into four
    // duplicate NGomez records at COTY. Say what actually happened and let them reload.
    try {
      await this.loadProgramAccess();

      AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
      AgFns.refreshGrid(this.sugmGridOptions, this.sugms);
      AgFns.refreshGrid(this.paGridOptions, this.programAccessRows);
    } catch (e) {
      console.error('afterSave refresh failed', e);
      await this.dialogService.okDialog(
        'Saved — but the page could not refresh',
        'The user was saved successfully. The Program Access view could not be reloaded, so it may ' +
          'be out of date. Reload the page to see the current state. Do not add the user again.',
      );
    }

    // Add-only convenience: offer to send the new user their join-Proximity invite
    // immediately. The Send Invite button on the footer remains for the no/skip case
    // and for re-sends later. Skipped when the new user has no email on file (the
    // invite endpoint would have nowhere to send to anyway) or when the account has
    // disabled AccountUser self-service (invites are meaningless in that state).
    if (wasAdded && this.proximityUser?.email?.trim() && this.account.allowAccountUserAccess) {
      const yes = await this.dialogService.askIfOk('Send Invitation', 'Send an email Invitation to allow this user to join Proximity?');
      if (yes) {
        await this.onSendInvite();
      }
    }

    // if saving changes to yourself, reload the page so AuthUser gets the changes
    if (this.proximityUser.id == this.authUser?.id || this.proximityUser.id == this.authUser?.actingAsUser?.id) {
      location.reload();
    }
  }

  private createNewUser() {
    this.proximityUser = this.dbSaveService.createEntity(ProximityUser, {
      activeStatusId: ActiveStatusEnum.Active,
      proximityRoleId: this.accountUserRole.id,
    });
    this.accountUser = this.dbSaveService.createEntity(AccountUser, {
      id: this.proximityUser.id,
      accountId: this.accountId,
      proximityUserId: this.proximityUser.id,
      genderId: 0,
    });
    this.proximityUser.entityAspect.setUnchanged();
    this.accountUser.entityAspect.setUnchanged();
    this.accountUser.entityAspect.validateEntity();
    this.proximityUser.entityAspect.validateEntity();
  }

  /**
   * Lookup-first add support: when the email field loses focus during add-mode, ask the
   * server whether an AccountUser with this email already exists in the current account.
   * On match, surface the existingUserMatch banner so the admin can switch to attaching
   * a PUG to the existing record rather than duplicating it. No-op for edit-mode and for
   * out-of-scope state. Cross-account matches are intentionally not returned by the server.
   */
  async onEmailBlur(email: string | null | undefined) {
    if (!this.isBeingAdded) return;
    if (this.outOfScope) return;
    if (this.emailLookupBusy) return;
    const trimmed = (email ?? '').trim();
    if (!trimmed) {
      this.existingUserMatch = null;
      return;
    }
    this.emailLookupBusy = true;
    try {
      const matches = await this.dbQueryService.findAccountUserForAdd(trimmed, this.accountId);
      // Multiple in-account matches with the same email do exist (legacy dup data) but the
      // first row is the AccountUser the admin should attach to. We surface a generic banner
      // either way; the admin clicks Switch and lands on that record.
      this.existingUserMatch = matches.length > 0 ? matches[0] : null;
    } finally {
      this.emailLookupBusy = false;
    }
  }

  /**
   * Refresh the add-mode Helpful Hints with the user's first name after they tab out of
   * the First Name field. Snapshotted (rather than read live) so the hints text doesn't
   * churn while they're still typing.
   */
  onFirstNameBlur(firstName: string | null | undefined) {
    this.firstNameForHints = (firstName ?? '').trim();
  }

  /**
   * Add-mode-only contextual tips, mirroring the Helpful Hints panel on the Program form
   * (program-info.sub.component.ts). Personalizes with the captured first name when
   * available; falls back to a neutral pronoun otherwise.
   */
  get helpfulHints(): string[] {
    if (!this.isBeingAdded || this.outOfScope) return [];
    const who = this.firstNameForHints || 'this user';
    const pronoun = this.firstNameForHints ? `${this.firstNameForHints}'s` : "this user's";
    return [
      `Assign a Shipping User Group unless shipments are made to ${pronoun} home address.`,
      `Enter a Personal Shipping Address if home shipments are allowed for ${who}`,
      `Include a Mobile number and click 'Accept SMS' if ${who} wants text updates on orders.`,
      'Make sure to set status to ACTIVE to make Proximity available.',
    ];
  }

  /**
   * Match-found banner action: discard the in-progress add and navigate to edit-mode for
   * the matched AccountUser. The Breeze unit-of-work entities created by createNewUser are
   * cleaned up by router teardown; we don't need explicit detachment.
   */
  switchToExistingUser() {
    if (!this.existingUserMatch) return;
    const id = this.existingUserMatch.accountUserId;
    this.existingUserMatch = null;
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-users', id]);
  }

  // note simple snapshotting to detect changes to 'added' record will not work on this form because we of two reasons
  //  1 - Address fields ( complex objects ) are not currently handled by snapshots
  //  2 - We are actually editing two different objects here - AccountUser and its underlying ProximityUser

  override navigateToValidationError(ee: EntityError) {
    // TODO: need to change tab for accountAddress errors
    UtilFns.focusInputByEntity('#topLevel', ee.entity, ee.propertyName);
  }
}
